import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import FilterBar from "@/components/feature/FilterBar";
import DramaCard, { DramaCardSkeleton } from "@/components/base/DramaCard";
import { Breadcrumbs, EmptyState } from "@/components/feature/PageShell";
import { categories } from "@/mocks/categories";
import { dramas } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ALL, PAGE_SIZE, defaultFilters, filterDramas, type FilterState } from "@/utils/catalog";

export default function CategoryPage() {
  const { type = "" } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const category = categories.find((item) => item.key === type);
  const pageParam = Number(searchParams.get("page") ?? "1");

  useEffect(() => {
    setPage(pageParam > 0 ? pageParam : 1);
  }, [pageParam]);

  useEffect(() => {
    setFilters(defaultFilters);
    setPage(1);
    window.scrollTo({ top: 0 });
  }, [type, location.key]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 380);
    return () => window.clearTimeout(timer);
  }, [filters, page, type]);

  const baseList = useMemo(
    () => (category ? dramas.filter((item) => item.category === category.key) : []),
    [category]
  );

  const list = useMemo(() => filterDramas(baseList, filters), [baseList, filters]);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = list.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  usePageMeta({
    title: category
      ? `${category.name}分类 - 日本伦理剧`
      : "全部分类 - 日本伦理剧",
    description: category
      ? `${category.name}是日本伦理剧的重点题材：${category.desc}。本站收录相关剧集资料、剧情梳理与编辑点评，可按地区、年份与评分筛选。`
      : "日本伦理剧全部题材分类，包含伦理剧情、伦理家庭、伦理爱情、伦理职场与年代剧等方向。",
    keywords: category
      ? `${category.name},日本伦理剧,${category.name}推荐,日本${category.name}电视剧`
      : "日本伦理剧分类,日本伦理电视剧分类,伦理题材影视",
  });

  const changePage = (next: number) => {
    setPage(next);
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params, { replace: true });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const resetAll = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page py-20">
          <EmptyState
            icon="ri-folder-unknow-line"
            title="这个分类暂时不存在"
            desc="可能是链接输入有误，或者该分类尚未开放。你可以回到首页，从六个题材入口重新选择。"
            action={
              <>
                <Link
                  to="/"
                  className="rounded-md bg-primary-900 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-800"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/ethics-drama"
                  className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 hover:border-accent-400"
                >
                  浏览伦理剧情
                </Link>
              </>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="container-page py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "日本伦理剧", to: "/category/ethics-drama" },
                { label: category.name },
              ]}
            />
            <div className="mt-6 grid gap-6 md:grid-cols-12 md:items-end">
              <div className="md:col-span-8">
                <p className="rule-label text-accent-700">{category.en.toUpperCase()} / CATEGORY</p>
                <h1 className="mt-3 font-heading text-[28px] font-semibold leading-tight text-foreground-950 md:text-[40px]">
                  {category.name}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-loose text-foreground-600">{category.longDesc}</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <p className="font-label text-[11px] tracking-[0.18em] text-foreground-400">ARCHIVE</p>
                <p className="mt-1 font-heading text-2xl font-semibold text-primary-900">
                  {baseList.length} <span className="text-sm font-normal text-foreground-500">部收录</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page py-8 md:py-12">
          <div className="grid gap-8 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="rule-label text-foreground-500">全部题材</p>
                <ul className="mt-3 space-y-1">
                  {categories.map((item) => (
                    <li key={item.key}>
                      <Link
                        to={`/category/${item.key}`}
                        className={`flex items-center justify-between rounded-md px-2.5 py-2 text-sm transition-colors ${
                          item.key === category.key
                            ? "bg-primary-900 font-medium text-background-50"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        {item.name}
                        <i
                          className={`ri-arrow-right-s-line ${
                            item.key === category.key ? "text-accent-300" : "text-foreground-300"
                          }`}
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-5">
                <p className="rule-label text-secondary-800">编辑建议</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                  先按评分筛出 9 分以上的条目，再用年份区间往回看，往往能发现同一题材在不同年代的处理差异。
                </p>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <FilterBar value={filters} onChange={(next) => { setFilters(next); setPage(1); }} />

              <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-foreground-500">
                  共 <span className="font-medium text-foreground-900">{list.length}</span> 部作品 · 第 {safePage} /{" "}
                  {totalPages} 页
                </p>
                <Link to="/search" className="text-xs text-accent-700 hover:underline">
                  没找到？试试搜索
                </Link>
              </div>

              {loading ? (
                <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <DramaCardSkeleton key={index} />
                  ))}
                </div>
              ) : visible.length === 0 ? (
                <div className="mt-6">
                  <EmptyState
                    title="没有符合筛选条件的作品"
                    desc="试着放宽年份或评分区间，或者把地区改回全部。也可以浏览当前分类的全部条目。"
                    action={
                      <button
                        type="button"
                        onClick={resetAll}
                        className="cursor-pointer rounded-md bg-primary-900 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-800"
                      >
                        重置筛选条件
                      </button>
                    }
                  />
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5">
                  {visible.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} showSummary />
                  ))}
                </div>
              )}

              {!loading && list.length > PAGE_SIZE ? (
                <div className="mt-10 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={safePage === 1}
                    onClick={() => changePage(safePage - 1)}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-accent-400 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => changePage(index + 1)}
                      className={`h-9 w-9 cursor-pointer rounded-md border text-sm transition-colors ${
                        safePage === index + 1
                          ? "border-primary-900 bg-primary-900 text-background-50"
                          : "border-background-300 text-foreground-700 hover:border-accent-400"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={safePage === totalPages}
                    onClick={() => changePage(safePage + 1)}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-accent-400 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              ) : null}

              {filters.region !== ALL || filters.yearRange !== ALL || filters.ratingRange !== ALL ? (
                <p className="mt-4 text-center text-[11px] text-foreground-400">
                  当前已应用筛选条件，排序方式：{filters.sort === "updated" ? "更新日期" : filters.sort === "hot" ? "热门度" : filters.sort === "rating" ? "评分" : "首播年份"}
                </p>
              ) : null}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}